import { ArrowLeft } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { PaginatedTable } from '@/components/ui/paginated-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocalAds } from '@/hooks/useInsights';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { LIA_INSIGHT_METRICS } from '@/lib/liaInsights';

/**
 * Full-list page for a single Local Inventory Ads insight — the destination
 * of the "View more" buttons on the Results and Ads summary cards. Reuses the
 * same `useLocalAds` data and shared column config as the summaries so the
 * table matches; it just renders the full range rather than the top 6.
 */
export function LocalAdsInsight() {
	const { metric } = useParams();
	const { shop, isLoading: shopLoading } = useShop();
	const { data, isLoading: adsLoading } = useLocalAds(shop?.id);
	const { shopPath } = useShopPath();
	const navigate = useNavigate();

	const config = LIA_INSIGHT_METRICS[metric];
	// Unknown metric slug (typo / stale link) — send them to the Ads home
	// rather than rendering an empty shell.
	if (!config) return <Navigate to={shopPath('/ads')} replace />;

	const loading = shopLoading || adsLoading;

	const goBack = () => {
		if (window.history.length > 1) navigate(-1);
		else navigate(shopPath('/ads'));
	};

	if (loading) {
		return (
			<div className="space-y-8">
				<Skeleton className="h-9 w-64" />
				<Skeleton className="h-80" />
			</div>
		);
	}

	// Ads aren't switched on (or no data) — nothing to show in full; the Ads
	// page owns activation, so bounce there.
	if (!data?.enabled) return <Navigate to={shopPath('/ads')} replace />;

	const rows = Array.isArray(data[config.dataKey]) ? data[config.dataKey] : [];

	return (
		<div className="space-y-8">
			<button
				type="button"
				onClick={goBack}
				className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition hover:text-foreground"
			>
				<ArrowLeft className="h-3.5 w-3.5" /> Back
			</button>

			<header>
				<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">{config.title}</h1>
				<p className="mt-2 max-w-2xl text-muted-foreground">{config.sub}</p>
			</header>

			<PaginatedTable rows={rows} columns={config.columns} emptyLabel={config.emptyLabel} pageSize={50} />
		</div>
	);
}
